import { fieldsForKind, formulaDependencies, rebindDriver } from '@agavi/blocks';

import type { VarBinding } from '@/components/task/variable-utils';
import type { ClipboardBlockItem } from '@/lib/clipboard';
import type { Block, BlockType, TaskVariable } from '@/types/experience';


import { rebindVarsInBlockData, renameVarInFormula } from '@/components/task/variable-utils';

/**
 * Reconciling a copied block with the task it is being pasted into.
 *
 * A block binds to its variables by NAME — `computeVariableValues` returns `Record<name, value>` —
 * but the value itself is seeded by the variable's ID (`deriveSeed` hashes it). Two variables that
 * look identical and are not the same variable therefore roll different numbers, which is what
 * makes this more than a string substitution:
 *
 *   - rename everything on collision, and a teacher who deliberately set up the same `{depth}` in
 *     both tasks gets a `depth_2` that disagrees with the rest of the destination task;
 *   - reuse everything on a name match, and a `{depth}` that meant 10–50 m silently becomes 0–5.
 *
 * So the comparison is on DEFINITION, and only a real mismatch is worth interrupting for.
 *
 * Pure and injectable: id generation, naming and the clock are all parameters, so the whole policy
 * is testable without a DOM, a store or a task editor.
 */

export interface VariableConflict {
  incoming: TaskVariable;
  existing: TaskVariable;
}

/**
 * `remap` — drop the incoming variable and bind the block to the destination's variable of that
 * name. The block's numbers become the destination's.
 * `rename` — bring the incoming variable along under a free name. The block keeps its own numbers.
 */
export type ConflictChoice = 'remap' | 'rename';

/**
 * EVERY FIELD THAT DECIDES THE NUMBERS, taken from the one place that lists them.
 *
 * `fieldsForKind` is the authority on what belongs to a variable of each kind, and it returns
 * all of them keyed, with the ones that do not belong set to `undefined`. Reading the list off
 * it means a setting added there cannot be silently left out of this comparison — which is
 * exactly how this came to compare four fields out of nine.
 */
const DECIDING_FIELDS = Object.keys({
  ...fieldsForKind('random'), ...fieldsForKind('constant'), ...fieldsForKind('equation'),
}) as (keyof TaskVariable)[];

/** Absent is absent, however it is spelled. A formula cleared in the editor is `''`, one that
 *  was never set is `undefined`, and a document round-tripped through Mongo can hold `null`. */
const absent = (v: unknown) => (v === null || v === '' ? undefined : v);

/**
 * Same variable for practical purposes: the same numbers out for the same seed in.
 *
 * It used to compare `min`, `max`, `step` and `formula`, which describes a RANDOM and nothing
 * else. Two constants therefore always matched — `{g} = 9.8` pasted into a task holding
 * `{g} = 3.7` was "the same variable", silently reused, and every answer computed from the
 * wrong one. So did a skewed random against a uniform one over the same range, and so did two
 * equations rounding to different decimals. All three are the same oversight: a comparison
 * written for one kind of variable and applied to all three.
 *
 * Takes a PARTIAL, so a caller comparing a definition that is not yet a whole variable — a
 * promotion decides the definition before the row exists — uses this one rather than writing
 * a second copy of the same comparisons.
 *
 * Says nothing about an equation's INPUTS, which two identical formulas can disagree about.
 * That needs both scopes in hand: see `sameVariableInContext`.
 */
export function sameDefinition(
  a: Partial<TaskVariable>,
  b: Partial<TaskVariable>,
): boolean {
  return DECIDING_FIELDS.every(k => absent(a[k]) === absent(b[k]));
}

/**
 * AND AN EQUATION IS ONLY THE SAME WHEN ITS INPUTS ARE.
 *
 * `{total} = {a} * 2` compares equal to another `{total} = {a} * 2` on every field there is,
 * and means something different when the two `{a}`s do. Reusing the destination's then rebinds
 * the pasted block to numbers nobody chose — the precise harm this module opens by refusing to
 * do, hidden one level down the dependency graph where the field comparison cannot see it.
 *
 * Recursive, because an input can be an equation too, and `seen` makes a cycle among the
 * formulas terminate rather than recurse forever — the same guard `variablesUsedByBlock` uses
 * on the same graph.
 *
 * A dependency the teacher chose to REMAP is treated as the same: remapping means "use this
 * task's one", so the equation reading it genuinely does resolve to the destination's value.
 * Without that, choosing remap for `{a}` would still import a redundant `total_2` computing
 * exactly what the destination's `total` already computes.
 */
export function sameVariableInContext(args: {
  a: Partial<TaskVariable>;
  b: Partial<TaskVariable>;
  /** Everything each side can resolve, by name. */
  aScope: Map<string, TaskVariable>;
  bScope: Map<string, TaskVariable>;
  /** Incoming variable id -> the teacher's choice, as `planVariableImport` receives it. */
  choices?: Record<string, ConflictChoice>;
  seen?: Set<string>;
}): boolean {
  const { a, b, aScope, bScope, choices = {}, seen = new Set<string>() } = args;
  if (!sameDefinition(a, b)) return false;
  if (!a.formula) return true;

  for (const dep of formulaDependencies(a.formula)) {
    if (seen.has(dep)) continue;
    seen.add(dep);
    const av = aScope.get(dep);
    const bv = bScope.get(dep);
    // Neither side defines it. The formula is equally unresolvable at both ends, which is a
    // problem the teacher already has and not a difference between the two variables.
    if (!av && !bv) continue;
    if (!av || !bv) return false;
    if (choices[av.id] === 'remap') continue;
    if (!sameVariableInContext({ a: av, b: bv, aScope, bScope, choices, seen })) return false;
  }
  return true;
}

/** Every distinct variable the copied blocks reference, in first-seen order. */
export function incomingVariables(items: ClipboardBlockItem[]): TaskVariable[] {
  const seen = new Set<string>();
  const out: TaskVariable[] = [];
  for (const item of items) {
    for (const v of item.variables) {
      if (seen.has(v.id)) continue;
      seen.add(v.id);
      out.push(v);
    }
  }
  return out;
}

/** The mismatches worth asking about: same name, different definition. */
export function findVariableConflicts(
  incoming: TaskVariable[],
  destination: TaskVariable[],
): VariableConflict[] {
  const byName = new Map(destination.map(v => [v.name, v]));
  const out: VariableConflict[] = [];
  for (const v of incoming) {
    const existing = byName.get(v.name);
    if (existing && !sameDefinition(existing, v)) out.push({ incoming: v, existing });
  }
  return out;
}

/** `depth` → `depth_2` → `depth_3`. Stays inside `/^[a-zA-Z][a-zA-Z0-9_]*$/`, which names must match. */
function freeVarName(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}_${n}`)) n++;
  return `${base}_${n}`;
}

export function planVariableImport(
  incoming: TaskVariable[],
  destination: TaskVariable[],
  choices: Record<string, ConflictChoice> = {},
  newVarId: () => string = () => `var_${crypto.randomUUID()}`,
): { bindings: VarBinding[]; toImport: TaskVariable[] } {
  const byName = new Map(destination.map(v => [v.name, v]));
  const taken = new Set(destination.map(v => v.name));
  const bindings: VarBinding[] = [];
  const toImport: TaskVariable[] = [];
  // The two sides as the dependency walk needs them: an equation is only the same variable
  // when the variables it reads are, and that question cannot be asked of two rows alone.
  const incomingScope = new Map(incoming.map(v => [v.name, v]));

  for (const v of incoming) {
    const existing = byName.get(v.name);
    const bindTo = (target: TaskVariable) =>
      bindings.push({ fromId: v.id, fromName: v.name, toId: target.id, toName: target.name });

    // Already there, and the same variable in every way that matters. Reuse it — which is still a
    // rewrite, because the block's chips carry the SOURCE task's variable id and would otherwise
    // keep seeding from a variable this task does not have.
    if (existing && sameVariableInContext({
      a: v, b: existing, aScope: incomingScope, bScope: byName, choices,
    })) { bindTo(existing); continue; }

    // A real conflict. Absent an explicit choice this imports rather than remaps: importing leaves
    // the pasted block behaving exactly as it did, where remapping silently changes its numbers, so
    // it is the one default that cannot quietly alter what a teacher already authored.
    if (existing && choices[v.id] === 'remap') { bindTo(existing); continue; }

    const name = freeVarName(v.name, taken);
    taken.add(name);
    // A FRESH id, never the incoming one. Both tasks may descend from the same duplicate and so
    // already share variable ids, and reusing one would collide with a variable of another name.
    const imported: TaskVariable = { ...v, id: newVarId(), name };
    toImport.push(imported);
    bindTo(imported);
  }

  /*
   * AND THE IMPORTED EQUATIONS HAVE TO FOLLOW THE RENAMES TOO.
   *
   * An equation references its inputs by NAME — `{a}*2` — and a collision renames an input to
   * `a_2`. The block's own chips and formulas were rewritten through these same bindings from
   * the start; the imported VARIABLES were copied verbatim, so an equation arrived still
   * reading `{a}` and quietly resolved against the DESTINATION's `a`, which is a different
   * variable with different numbers. That is precisely the silent change of a teacher's
   * authored answers this module's header says it refuses to make, made one level down from
   * where it was being watched for.
   *
   * A SECOND PASS, after every binding is decided: a dependency's new name can be settled
   * later in the loop above than the equation that reads it, so rewriting inline would use a
   * name that was still being chosen.
   */
  const renames = bindings.filter(b => b.fromName !== b.toName);
  if (renames.length > 0) {
    for (const v of toImport) {
      if (!v.formula) continue;
      v.formula = renames.reduce(
        (formula, b) => renameVarInFormula(formula, b.fromName, b.toName),
        v.formula,
      );
    }
  }

  return { bindings, toImport };
}

/**
 * REWIRE A FOLLOWER TO THE LEADER THAT TRAVELLED WITH IT, OR UNLINK IT.
 *
 * A scenario set lives inside ONE task — `ScenarioLinkMenu` searches the current task's block list
 * and nothing else — so a `followsBlockId` that crosses a task boundary names a block that is not
 * there. Before this existed, nothing rewrote or removed it: a pasted follower kept pointing at
 * its old leader's id and was quietly broken, with its pager empty and its rows unreachable.
 *
 * Three cases, and the last is the one worth explaining:
 *
 * 0. **The leader is already in the destination** — a paste back into the set's own task. Leave
 *    the link alone. This is why the test is RESOLVABILITY rather than "did the leader travel":
 *    checking only the id map would unlink a follower pasted beside its own leader, which works
 *    today and must keep working. It also catches the reverse case — pasting back into the
 *    original task after the leader was deleted correctly unlinks, where a same-task check would
 *    have left the link dangling.
 *
 * 1. **The leader came too** (the whole set was pasted) — point at its new id. Block ids are
 *    re-minted on paste; `scenarioId`s are NOT, and must not be. A variant id is scoped to its
 *    leader (`drawnScenarioId` keys the roll off the leader's block id), so pasting the same set
 *    twice into one task gives two independent sets whose variant ids coincide harmlessly. Keeping
 *    them is also what lets the rows that travelled still match their variants.
 *
 * 2. **The leader did not come** — do exactly what UNLINKING does in the editor: drop
 *    `followsBlockId` and `scenarioBranches` and keep the block's own answer setup. This is
 *    deliberately not a flatten-the-active-row operation. Matching `linkToLeader(undefined)` means
 *    a paste-on-its-own and a paste-then-unlink produce the identical block, so there is no second
 *    set of semantics to learn, document or keep in step — and no per-block registry of how to
 *    collapse a row, which is the thing that would have had to stay correct as blocks change.
 *
 *    The cost, stated because it is real: a teacher who authored every answer inside the rows gets
 *    a block whose base setup is stale or empty. That is already true of unlink, so the paste
 *    prompt is where it should be said, not here.
 */
function relinkScenario(block: Block, idMap: Map<string, string>, destination: Block[]): Block {
  const data = block.data as { followsBlockId?: string } | undefined;
  const follows = data?.followsBlockId;
  if (!follows) return block;

  if (destination.some(b => b.id === follows)) return block;

  const movedLeaderId = idMap.get(follows);
  if (movedLeaderId) {
    return { ...block, data: { ...block.data, followsBlockId: movedLeaderId } as Block['data'] };
  }
  const { followsBlockId: _dropped, scenarioBranches: _rows, ...rest } =
    block.data as Record<string, unknown>;
  return { ...block, data: rest as Block['data'] };
}

export function applyBlockPaste(args: {
  items: ClipboardBlockItem[];
  blocks: Block[];
  variables: TaskVariable[];
  /**
   * Everything the destination can already RESOLVE — the task's variables plus the experience's.
   *
   * Separate from `variables` because the two are used for different things: this one decides
   * what an incoming name matches and which names are taken, and `variables` is the list an
   * import is appended to. An experience variable must do the first and never the second.
   *
   * Defaults to `variables`, which is what it was before the experience scope existed.
   */
  inForce?: TaskVariable[];
  /**
   * Every task the destination can see, for rebinding a DRIVEN link's cross-task address.
   *
   * OPTIONAL, and absent means leave those links exactly as they are. The destructive reading
   * — "no tasks, so nothing resolves, so let go of everything" — is delete-by-omission wearing
   * a new hat, and the honest failure for a caller that does not know the task list is to
   * change nothing. An address left unrebound that no longer resolves is reported by the
   * block's own issue list, which is recoverable; a link dropped because an argument was
   * missing is not.
   */
  tasks?: Parameters<typeof rebindDriver>[0];
  /** Paste after this index; appended to the end when absent. */
  insertAfterIndex?: number;
  choices?: Record<string, ConflictChoice>;
  autoName: (type: BlockType, blocks: Block[]) => string;
  newBlockId?: () => string;
  newVarId?: () => string;
  now?: () => string;
}): { blocks: Block[]; variables: TaskVariable[]; pasted: Block[]; insertIdx: number } {
  const {
    items, blocks, variables, inForce, tasks, insertAfterIndex, choices = {}, autoName,
    newBlockId = () => `blk_${crypto.randomUUID()}`,
    newVarId = () => `var_${crypto.randomUUID()}`,
    now = () => new Date().toISOString(),
  } = args;

  const { bindings, toImport } = planVariableImport(
    incomingVariables(items), inForce ?? variables, choices, newVarId,
  );

  // Names are unique within a task and the save rejects duplicates, so the pool grows as blocks are
  // pasted — two copies of the same block in one paste must not both claim the source's name.
  const pasted: Block[] = [];
  /** Source id -> pasted id, so the second pass can rewire links that travelled with the group. */
  const idMap = new Map<string, string>();
  let namePool = blocks;
  for (const item of items) {
    const source = item.block;
    // A custom name is worth keeping (it is what Flow conditions refer to); an auto-name is not
    // worth fighting over, and either way a taken name falls back to the next free auto-name.
    const name = source.name && !namePool.some(b => b.name === source.name)
      ? source.name
      : autoName(source.type, namePool);
    const block: Block = {
      ...source,
      id: newBlockId(),
      // Provenance, exactly as duplication records it. `authorId` rides along untouched — the
      // original creator is never overwritten by a copy.
      heritageId: source.id,
      createdAt: now(),
      name,
      data: rebindVarsInBlockData(source.data, bindings),
    };
    pasted.push(block);
    idMap.set(source.id, block.id);
    namePool = [...namePool, block];
  }

  /*
   * TWO LINKS TO REWIRE, and they resolve against different things. A scenario names a block
   * in this task, so `relinkScenario` searches the destination's own list; a driver is a
   * cross-task address, so `rebindDriver` goes through `localAddressFor` and the whole task
   * list. They are deliberately not merged: one of them drops its rows when it lets go and the
   * other must keep every version.
   */
  const relinked = pasted
    .map(b => relinkScenario(b, idMap, blocks))
    .map(b => (tasks
      ? { ...b, data: rebindDriver(tasks, b.data as never) as Block['data'] }
      : b));

  const insertIdx = insertAfterIndex !== undefined ? insertAfterIndex + 1 : blocks.length;
  const next = [...blocks.slice(0, insertIdx), ...relinked, ...blocks.slice(insertIdx)]
    .map((b, i) => ({ ...b, order: i }));

  return { blocks: next, variables: [...variables, ...toImport], pasted: relinked, insertIdx };
}
